import {
  type CyclesServerMessage,
  type CyclesSettings,
  type CyclesSnapshot,
  enqueueTurn,
  type Heading,
} from '@xox/shared'
import { create } from 'zustand'
import { SYNC_THROTTLE_MS } from '../cycles/anim'
import {
  applyTick,
  type LiveRound,
  liveFromRound,
  type PendingTurn,
  predictedHeading,
  reconcilePending,
  relativeTurn,
} from '../cycles/sim'
import { type Effect, feedback } from '../lib/feedback'
import type { ConnectionStatus, RoomSocket } from '../lib/socket'
import {
  type CommonView,
  commonActions,
  initialCommonView,
  openRoomSocket,
  reduceCommon,
} from './shared'

export interface CyclesView extends CommonView {
  snapshot: CyclesSnapshot | null
  /** The running (or just finished) round, kept up to date from tick frames. */
  live: LiveRound | null
  /** Own turns sent but not yet confirmed; the arena renders as if they will apply. */
  pending: PendingTurn[]
}

interface CyclesStore extends CyclesView {
  code: string | null
  status: ConnectionStatus
  connect: (code: string) => void
  disconnect: () => void
  takeSeat: () => void
  leave: () => void
  start: () => void
  next: () => void
  settings: (patch: Partial<CyclesSettings>) => void
  /** Absolute heading (keys, swipe). */
  turn: (h: Heading) => void
  /** Relative to where the cycle is about to head (tap left / tap right). */
  steer: (side: 'left' | 'right') => void
  sync: () => void
  react: (emoji: Parameters<ReturnType<typeof commonActions>['react']>[0]) => void
  expireReaction: (id: string) => void
  dismissError: () => void
}

function mine(view: CyclesView) {
  return view.live?.cycles.find((c) => c.id === view.playerId) ?? null
}

export function reduceCyclesMessage(
  view: CyclesView,
  msg: CyclesServerMessage,
  now = Date.now(),
): Partial<CyclesView> {
  const common = reduceCommon(view, msg, now)
  if (common) return common
  switch (msg.type) {
    case 'state': {
      const s = msg.snapshot
      if (view.snapshot && s.seq < view.snapshot.seq) return {}
      const patch: Partial<CyclesView> = { snapshot: s, closedReason: null }
      if (!s.round || s.phase === 'lobby') return { ...patch, live: null, pending: [] }
      const live = view.live
      // A snapshot only replaces the live mirror when it is at least as new (a state frame can
      // travel through Redis while tick frames come straight from the loop).
      if (!live || live.no !== s.round.no || s.round.tick >= live.tick) {
        return {
          ...patch,
          live: liveFromRound(s.round),
          pending: s.phase === 'racing' && live?.no === s.round.no ? view.pending : [],
        }
      }
      return patch
    }
    case 'tick': {
      if (!view.live || !view.snapshot) return {}
      const next = applyTick(view.live, msg, view.snapshot.tickMs)
      if (next === view.live) return {}
      const before = mine(view)
      const after = next.cycles.find((c) => c.id === view.playerId)
      const pending =
        before && after ? reconcilePending(view.pending, before.heading, after, next.tick) : []
      return { live: next, pending }
    }
    default:
      return {}
  }
}

/** Own crash thuds, rivals' crashes tick; phase changes cue the countdown, the start and the end. */
export function deriveCyclesEffects(
  prev: CyclesView,
  next: CyclesView,
  msg: CyclesServerMessage,
): Effect[] {
  if (msg.type === 'reaction') return ['react']
  if (msg.type === 'tick') {
    const deaths = next.live?.deaths ?? []
    if (deaths.length === 0 || next.live === prev.live) return []
    return [deaths.includes(next.playerId ?? '') ? 'crash' : 'tick']
  }
  if (msg.type !== 'state') return []
  const a = prev.snapshot
  const b = next.snapshot
  if (!b) return []
  if (a && b.seq > a.seq && b.racers.length > a.racers.length) return ['seat']
  if (!a || b.seq <= a.seq || a.phase === b.phase) return []
  if (b.phase === 'racing') return ['go']
  const me = next.playerId
  if (b.phase === 'roundEnd') return me && b.lastRound?.placeById[me] === 1 ? ['cheer'] : []
  if (b.phase === 'results') {
    if (!me || !(me in b.scores)) return ['cheer']
    const top = Math.max(...Object.values(b.scores))
    return b.scores[me] === top ? ['cheer', 'win'] : ['lose']
  }
  return []
}

let socket: RoomSocket | null = null
let lastSyncAt = 0

export const useCycles = create<CyclesStore>((set, get) => ({
  ...initialCommonView,
  code: null,
  status: 'closed',
  snapshot: null,
  live: null,
  pending: [],

  connect(code) {
    if (socket && get().code === code) return
    socket?.close()
    set({
      ...initialCommonView,
      code,
      snapshot: null,
      live: null,
      pending: [],
      status: 'connecting',
    })
    socket = openRoomSocket<CyclesView, CyclesServerMessage>(code, '/ws/cycles', {
      get,
      set,
      setStatus: (status) => set({ status }),
      reduce: reduceCyclesMessage,
      effects: deriveCyclesEffects,
      expireReaction: (id) => get().expireReaction(id),
      after: (_msg, patch) => {
        if (patch.live?.gap) get().sync()
      },
    })
  },

  disconnect() {
    socket?.close()
    socket = null
    set({ code: null, snapshot: null, live: null, pending: [], status: 'closed', reactions: [] })
  },

  ...commonActions<CyclesStore>(() => socket, set),
  start() {
    socket?.send({ type: 'start' })
  },
  next() {
    socket?.send({ type: 'next' })
  },
  settings(patch) {
    socket?.send({ type: 'settings', ...patch })
  },
  turn(h) {
    const view = get()
    const me = mine(view)
    const phase = view.snapshot?.phase
    if (!me?.alive || !view.live || (phase !== 'racing' && phase !== 'countdown')) return
    const before = view.pending.map((p) => p.h)
    const after = enqueueTurn(before, h)
    if (after.length === before.length && after.every((x, i) => x === before[i])) return
    const tick = view.live.tick
    set({
      pending: after.map((x) => view.pending.find((p) => p.h === x) ?? { h: x, tick }),
    })
    feedback('turn')
    socket?.send({ type: 'turn', h })
  },
  steer(side) {
    const view = get()
    const me = mine(view)
    if (!me) return
    get().turn(
      relativeTurn(
        side,
        predictedHeading(
          me.heading,
          view.pending.map((p) => p.h),
        ),
      ),
    )
  },
  sync() {
    const now = Date.now()
    if (now - lastSyncAt < SYNC_THROTTLE_MS) return
    lastSyncAt = now
    socket?.send({ type: 'sync' })
  },
}))

/** Server clock as this room's socket sees it. */
export function cyclesServerNow(): number {
  return Date.now() + useCycles.getState().clockOffset
}
